import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SITE_URL = 'https://www.byte-forge.it';
const SITE_NAME = 'Byte Forge';
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/logo.png`;

export interface SEOProps {
  /** Seitentitel ohne Marken-Suffix (wird automatisch angehängt). */
  title: string;
  /** Meta-Description, idealerweise 120-160 Zeichen. */
  description: string;
  /** Pfad ohne Domain, z. B. "/dynamics365". Wird für canonical & og:url genutzt. */
  path: string;
  /** Optionale, kommagetrennte Keywords (ergänzend, Google gewichtet dies kaum noch, schadet aber nicht). */
  keywords?: string;
  /** Optionales Vorschaubild für Open Graph / Twitter Cards. */
  image?: string;
  /** Optionales strukturiertes Datenobjekt (JSON-LD), wird als <script type="application/ld+json"> gerendert. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Setzt "noindex, nofollow" (z. B. für Danke-/Fehlerseiten). */
  noindex?: boolean;
}

/**
 * Zentrale SEO-Komponente: setzt pro Route einen eindeutigen Title, eine
 * Meta-Description, kanonische URL, Open-Graph-/Twitter-Card-Tags sowie
 * optionale strukturierte Daten (JSON-LD). Muss in jeder Seiten-Komponente
 * eingebunden werden, da eine Single-Page-App sonst für alle Routen
 * denselben, statischen <title>/<meta> aus index.html verwendet.
 */
const SEO: React.FC<SEOProps> = ({
  title,
  description,
  path,
  keywords,
  image = DEFAULT_OG_IMAGE,
  jsonLd,
  noindex = false,
}) => {
  const { i18n } = useTranslation();
  const canonicalUrl = `${SITE_URL}${path}`;
  const fullTitle = `${title} | ${SITE_NAME}`;
  const locale = 'de'; //i18n.language?.startsWith('de') ? 'de' : 'en';

  return (
    <Helmet>
      <html lang={locale} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <meta name="author" content={SITE_NAME} />
      <meta name="copyright" content="Byte Forge UG" />
      <meta name="revisit-after" content="7 days" />
      <meta name="audience" content="all" />
      <meta name="page-topic" content={title} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={locale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
